import React, { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useResultComments } from "../../data/useResultsComments";
import { Box, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { ResultCommentUI } from "./ResultCommentUI";
import { AddButton } from "../../shared/FormLayout/IconButtons/AddButton";
import { NewCommentDialog } from "./NewCommentDialog";
import Alert, { AlertColor } from "@mui/material/Alert";
import { Snackbar } from "../../shared/Snackbar";
import { MessageCard, MessageType } from "../../shared/MessageCard";
import { AccessControlContext } from "../../contexts/AccessControlContext";
interface Props {
  captureId: number;
  customerId?: number;
}

export function ResultComments(props: Props) {
  const { t } = useTranslation();
  const { captureId, customerId } = props;

  const accessControlContext = useContext(AccessControlContext);
  const userCanAddResultComments = accessControlContext.canI("add", "resultComments", customerId);

  const [newCommentDialogOpen, setNewCommentDialogOpen] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  const comments = useResultComments(captureId);

  function handleAddButtonClick() {
    setNewCommentDialogOpen(true);
  }

  function handleSuccess(successMessage: string) {
    setSnackbarSeverity("success");
    comments.refetch();
    setSnackbarText(successMessage);
    setShowSnackbar(true);
  }

  function handleError(errorMessage: string) {
    setSnackbarSeverity("error");
    setSnackbarText(errorMessage);
    setShowSnackbar(true);
  }

  const handleSnackbarClose = useCallback(() => {
    setShowSnackbar(false);
  }, []);

  if (comments.isLoading)
    return (
      <>
        <Stack spacing={2}>
          {[...Array(5).keys()].map((i) => (
            <Box px={3} key={`skeleton-row-${i}`}>
              <Skeleton variant="rectangular" height={40} />
            </Box>
          ))}
        </Stack>
      </>
    );

  if (comments.isError)
    return (
      <MessageCard
        title={t("common.dataFetchError_short", { val: t("common.comments") })}
        content={t("common.dataFetchError", { val: t("common.comments") })}
        messageType={MessageType.Error}
      />
    );

  return (
    <>
      <Stack spacing={2} divider={<Divider />}>
        {comments.data && comments.data.comments?.length > 0 ? (
          comments.data?.comments.map((comment) => (
            <ResultCommentUI
              captureId={captureId}
              comment={comment}
              onSuccess={handleSuccess}
              onError={handleError}
              customerId={customerId}
            />
          ))
        ) : (
          <Box display="flex" justifyContent="center">
            <Typography variant="subtitle2" color="textSecondary">
              {t("common.commentsEmpty")}
            </Typography>
          </Box>
        )}
        {userCanAddResultComments && (
          <Box display="flex" justifyContent="center">
            <AddButton onClick={handleAddButtonClick} />
          </Box>
        )}
      </Stack>
      {userCanAddResultComments && (
        <NewCommentDialog
          captureId={captureId}
          open={newCommentDialogOpen}
          onSuccess={handleSuccess}
          onError={handleError}
          onClose={() => setNewCommentDialogOpen(false)}
        />
      )}
      <Snackbar open={showSnackbar} onClose={handleSnackbarClose}>
        <Alert severity={snackbarSeverity} variant="filled">
          {snackbarText}
        </Alert>
      </Snackbar>
    </>
  );
}
